import {
  cacheShelf,
  getCachedShelf,
  getCachedTripBundle,
  isOnline,
  loadShelfWithInstances,
} from '../store.js';
import { formatDateIT } from '../utils/format.js';
import { escapeHtml } from '../utils/dom.js';
import { fetchWithTimeout } from '../utils/network-fetch.js';

import { tripCoverUrl } from '../utils/trip-covers.js';

function cardActions(trip, plan, instanceId) {
  if (plan?.status === 'draft') {
    return `<a class="btn-primary shelf-card__cta" href="#/new/plan/${trip.id}">Continua rotta</a>`;
  }
  if (!instanceId) {
    return `<a class="btn-primary shelf-card__cta" href="#/trip/${trip.id}/start">Avvia viaggio</a>`;
  }
  return `<a class="btn-primary shelf-card__cta" href="#/trip/${trip.id}/days">Apri viaggio</a>`;
}

function renderShelfCards(shelf, { offlineTripIds = new Set(), stale = false } = {}) {
  if (!shelf.length) {
    return `<p class="wizard-hint">Nessun viaggio ancora. Crea la tua prima rotta con il pulsante sopra.</p>`;
  }

  return shelf
    .map(({ trip, plan, instanceId }) => {
      const dates = `${formatDateIT(trip.date_start)} – ${formatDateIT(trip.date_end)}`;
      const cover = tripCoverUrl(trip.country);
      const statusLabel = plan?.status === 'draft' ? 'Bozza' : 'Pubblicata';
      const offlineBadge = offlineTripIds.has(trip.id)
        ? '<span class="shelf-card__offline-badge" title="Disponibile offline">📴 Offline</span>'
        : '';
      const staleBadge = stale
        ? '<span class="shelf-card__stale-badge" title="Dati in cache">⏳</span>'
        : '';
      return `
        <article
          class="shelf-card shelf-card--has-cover"
          style="--shelf-cover: url('${cover.replace(/'/g, '%27')}')"
        >
          <div class="shelf-card__overlay">
            <div class="shelf-card__badges">${offlineBadge}${staleBadge}</div>
            <h2 class="shelf-card__title">${escapeHtml(trip.title)}</h2>
            <p class="shelf-card__meta">${escapeHtml(trip.country)} · ${dates}</p>
            <p class="shelf-card__plan">Piano v${plan?.version ?? '?'} · ${statusLabel}</p>
            ${cardActions(trip, plan, instanceId)}
          </div>
        </article>
      `;
    })
    .join('');
}

async function offlineTripIdSet(shelf) {
  const ids = new Set();
  await Promise.all(
    shelf.map(async ({ trip }) => {
      const bundle = await getCachedTripBundle(trip.id);
      if (bundle) ids.add(trip.id);
    }),
  );
  return ids;
}

function paintShelfList(listEl, loadingEl, shelfHtml) {
  loadingEl.hidden = true;
  listEl.hidden = false;
  listEl.innerHTML = shelfHtml;
}

export async function renderShelf(container, { onLogout }) {
  container.innerHTML = `
    <div class="app-shell app-shell--shelf">
      <header class="app-header app-header--shelf">
        <div class="app-header__brand">
          <img class="app-header__logo" src="/pwa-icon.svg" width="36" height="36" alt="" />
          <div>
            <h1 class="app-header__title">Metis</h1>
            <p class="app-header__subtitle">I tuoi viaggi</p>
          </div>
        </div>
        <button type="button" class="btn-ghost btn-ghost--sm" id="logout-btn">Esci</button>
      </header>
      <main class="app-main app-main--shelf">
        <a class="btn-primary shelf-new" href="#/new/plan">+ Nuova rotta</a>
        <p class="shelf-sync-banner" id="shelf-sync-banner" hidden></p>
        <p class="shelf-loading">Caricamento…</p>
        <div id="shelf-list" class="shelf-list" hidden></div>
      </main>
    </div>
  `;

  container.querySelector('#logout-btn')?.addEventListener('click', onLogout);

  const listEl = container.querySelector('#shelf-list');
  const loadingEl = container.querySelector('.shelf-loading');
  const syncBanner = container.querySelector('#shelf-sync-banner');

  const cachedEntry = await getCachedShelf();
  if (cachedEntry?.shelf) {
    const offlineIds = await offlineTripIdSet(cachedEntry.shelf);
    paintShelfList(
      listEl,
      loadingEl,
      renderShelfCards(cachedEntry.shelf, { offlineTripIds: offlineIds, stale: true }),
    );
    syncBanner.hidden = false;
    syncBanner.textContent = isOnline()
      ? 'Aggiornamento in corso…'
      : `Dati salvati il ${formatDateIT(cachedEntry.cachedAt?.slice(0, 10) ?? '')}`;
  }

  if (!isOnline()) {
    if (!cachedEntry?.shelf) {
      loadingEl.hidden = true;
      listEl.hidden = false;
      listEl.innerHTML = `<p class="status-error">Nessun viaggio in cache. Connettiti per caricare lo scaffale.</p>`;
    }
    return;
  }

  try {
    const shelf = await fetchWithTimeout(loadShelfWithInstances(), 6000);
    await cacheShelf(shelf);
    const offlineIds = await offlineTripIdSet(shelf);
    paintShelfList(listEl, loadingEl, renderShelfCards(shelf, { offlineTripIds: offlineIds }));
    syncBanner.hidden = true;
  } catch (error) {
    if (cachedEntry?.shelf) {
      syncBanner.hidden = false;
      syncBanner.textContent = 'Non aggiornato — mostro i dati salvati';
      return;
    }
    loadingEl.hidden = true;
    listEl.hidden = false;
    listEl.innerHTML = `<p class="status-error">Errore: ${escapeHtml(error.message)}</p>`;
  }
}
